import type { CastCharacter } from "@/lib/castAnalysisTypes";
import { normalizeCharacter } from "@/lib/castAnalysisNormalize";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export type NarratorObservations = {
  description?: string;
  tone?: string;
  voice_direction?: string;
};

export type CastChunkAnalysisOk = {
  success: true;
  book_id: string;
  chunk_id: string;
  chunk_index: number;
  chunks_total: number;
  narrator_observations: NarratorObservations;
  characters: CastCharacter[];
};

export type CastChunkAnalysisErr = {
  success: false;
  error: string;
  stage?: string;
  raw_preview?: string;
};

export function parseCastChunkWebhookPayload(
  payload: unknown,
): CastChunkAnalysisOk | CastChunkAnalysisErr {
  if (!isRecord(payload)) return { success: false, error: "Invalid response" };
  if (payload.success === false) {
    const err =
      typeof payload.error === "string" && payload.error.trim()
        ? payload.error.trim()
        : "Chunk cast analysis failed";
    const stage = typeof payload.stage === "string" && payload.stage.trim() ? payload.stage.trim() : undefined;
    const raw_preview =
      typeof payload.raw_preview === "string" && payload.raw_preview.trim() ? payload.raw_preview.trim() : undefined;
    return { success: false, error: err, stage, raw_preview };
  }
  const book_id = typeof payload.book_id === "string" ? payload.book_id.trim() : "";
  const chunk_id = typeof payload.chunk_id === "string" ? payload.chunk_id.trim() : "";
  const chunk_index = typeof payload.chunk_index === "number" && Number.isFinite(payload.chunk_index) ? Math.floor(payload.chunk_index) : 0;
  const chunks_total =
    typeof payload.chunks_total === "number" && Number.isFinite(payload.chunks_total) ? Math.max(1, Math.floor(payload.chunks_total)) : 1;
  if (!book_id || !chunk_id) return { success: false, error: "Missing book_id or chunk_id" };

  let narrator_observations: NarratorObservations = {};
  if (isRecord(payload.narrator_observations)) {
    const o = payload.narrator_observations;
    narrator_observations = {
      description: typeof o.description === "string" ? o.description.trim() : "",
      tone: typeof o.tone === "string" ? o.tone.trim() : "",
      voice_direction: typeof o.voice_direction === "string" ? o.voice_direction.trim() : "",
    };
  }

  const rawChars = Array.isArray(payload.characters) ? payload.characters : [];
  const characters: CastCharacter[] = [];
  rawChars.forEach((c, i) => {
    const row = normalizeCharacter(c, i);
    if (row) characters.push(row);
  });

  return {
    success: true,
    book_id,
    chunk_id,
    chunk_index,
    chunks_total,
    narrator_observations,
    characters,
  };
}
